import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Barcode,
  CalendarCog,
  HandCoins,
  Package,
  Plus,
  Search,
  Trash2,
  TriangleAlert,
  CalendarClock,
  Truck,
} from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import type { ExpiringBatch, LowStockItem, Product } from '@/lib/types'
import { formatQuantity, formatUsd } from '@/lib/money'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, EmptyState, Modal, Select, Tabs } from '@/components/ui/primitives'
import { ProductEditor } from '@/pages/ProductEditor'
import { SettleDialog, type Settleable } from '@/components/SettleDialog'
import { LabelDialog } from '@/pages/LabelDialog'
import { WasteDialog, WasteHistory } from '@/pages/WasteDialog'
import { useSession } from '@/stores/session'
import { Permission } from '@/lib/permissions'
import { ReverseButton, ReverseDialog, ReversedBadge } from '@/components/ReverseDialog'
import { t } from '@/i18n'
import { ListFilters, Pager, NO_FILTERS, type ListFilterState } from '@/components/ListControls'
import { usePagedList } from '@/lib/paging'

type TabValue = 'all' | 'low' | 'expiring' | 'waste' | 'deliveries'

interface DeliveryRow {
  id: number
  number: string
  receiptDate: string
  supplierInvoiceNumber: string | null
  supplierName: string | null
  status: 'Draft' | 'Posted' | 'Cancelled'
  totalCostUsd: number
  lineCount: number
  /** What the delivery held, and what each line was charged at. */
  lines: {
    productId: number
    productName: string | null
    quantity: number
    unitCostUsd: number
    /** The invoice price plus this line's share of the carriage on the load. */
    landedUnitCostUsd: number
  }[]
  /** Posted, taken on credit and not yet settled — so still owed to the supplier. */
  isOutstanding: boolean
  settledAtUtc: string | null
  postedAtUtc: string | null
  isReversed: boolean
  reversalReason: string | null
  reversedBy: string | null
}

export function InventoryPage() {
  const canManageCatalog = useSession((state) => state.can)(Permission.ManageCatalog)
  const canViewReports = useSession((state) => state.can)(Permission.ViewReports)
  const canRecordWaste = useSession((state) => state.can)(Permission.RecordWaste)
  const canManageStock = useSession((state) => state.can)(Permission.ManageStock)

  const [tab, setTab] = useState<TabValue>('all')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<Product | null>(null)
  /** The batch whose expiry date is being corrected. */
  const [datingBatch, setDatingBatch] = useState<ExpiringBatch | null>(null)
  /** The delivery being paid off, if any. */
  const [settling, setSettling] = useState<Settleable | null>(null)
  const [isEditorOpen, setEditorOpen] = useState(false)
  /** The product whose barcode and labels are being worked on. */
  const [labelling, setLabelling] = useState<Product | null>(null)
  /** The product being written off. */
  const [wasting, setWasting] = useState<Product | null>(null)
  /** The posted delivery being unposted. */
  const [unposting, setUnposting] = useState<DeliveryRow | null>(null)
  const [deliveryFilters, setDeliveryFilters] = useState<ListFilterState>(NO_FILTERS)
  /** Which delivery has its contents open. One at a time keeps the list readable. */
  const [openDelivery, setOpenDelivery] = useState<number | null>(null)
  const [lowSearch, setLowSearch] = useState('')
  const [expiringSearch, setExpiringSearch] = useState('')
  const [expiringDays, setExpiringDays] = useState(30)

  const products = usePagedList<Product>(['products'], '/api/products', { search })

  const lowStock = usePagedList<LowStockItem>(
    ['stock', 'low'],
    '/api/stock/low',
    { search: lowSearch },
    { enabled: canViewReports },
  )

  const expiring = usePagedList<ExpiringBatch>(
    ['stock', 'expiring'],
    '/api/stock/expiring',
    { days: expiringDays, search: expiringSearch },
    { enabled: canViewReports },
  )

  const deliveries = usePagedList<DeliveryRow>(
    ['goods-receipts'],
    '/api/goods-receipts',
    { from: deliveryFilters.from, to: deliveryFilters.to, search: deliveryFilters.search },
    { enabled: canManageStock },
  )

  const tabs = useMemo(
    () =>
      [
        { value: 'all' as const, label: t('Products') },
        ...(canViewReports
          ? [
              // The whole count, not the page's — the tab badge is how many need attention.
              { value: 'low' as const, label: t('Low stock'), count: lowStock.total ?? undefined },
              { value: 'expiring' as const, label: t('Expiring'), count: expiring.total ?? undefined },
            ]
          : []),
        ...(canRecordWaste ? [{ value: 'waste' as const, label: t('Written off') }] : []),
        ...(canManageStock ? [{ value: 'deliveries' as const, label: t('Deliveries') }] : []),
      ] satisfies readonly { value: TabValue; label: string; count?: number }[],
    [canViewReports, canRecordWaste, canManageStock, lowStock.total, expiring.total],
  )

  function openEditor(product: Product | null) {
    setEditing(product)
    setEditorOpen(true)
  }

  return (
    <div className="pb-page mx-auto w-full max-w-5xl px-4 py-6 sm:px-6">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t('Inventory')}</h1>
          <p className="mt-0.5 text-sm text-[var(--text-secondary)]">
            {t('Stock is the sum of its batches, so quantity, cost and expiry always agree.')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* A link, not a button: it navigates, so it should be middle-clickable and
              open-in-new-tab like any other route. Styled to match the primary button. */}
          <Link
            to="/inventory/receive"
            className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[var(--accent)] px-4 text-sm font-medium text-[var(--accent-contrast)] hover:opacity-90"
          >
            <Truck className="h-4 w-4" />
            {t('Add stock')}
          </Link>

          {/* For setting a catalogue up before any stock arrives — pricing a shelf, printing
              labels, getting the barcodes on. The cost typed here is a placeholder until the
              first delivery, which is where a real one comes from, and the form says so. */}
          {canManageCatalog && (
            <Button variant="secondary" onClick={() => openEditor(null)}>
              <Plus className="h-4 w-4" />
              {t('New product')}
            </Button>
          )}
        </div>
      </header>

      <div className="mb-4">
        <Tabs tabs={tabs} value={tab} onChange={setTab} />
      </div>

      {tab === 'all' && (
        <>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('Search by name, SKU, or scan a barcode')}
            leading={<Search className="h-4 w-4" />}
            wrapperClassName="mb-4"
            // A wedge scanner types the code then Enter; there is nothing to submit here
            // because the query re-runs as the value changes.
            autoComplete="off"
          />

          {products.page.length === 0 ? (
            <EmptyState
              icon={Package}
              title={search ? 'Nothing matched' : 'No products yet'}
              description={
                search
                  ? 'Search matches the start of a name or SKU, or an exact barcode.'
                  : 'Add stock to create products as the delivery that brings them in, so each starts with a cost that is real — or add one on its own to price a shelf before anything arrives.'
              }
              action={
                canManageCatalog && !search ? (
                  <div className="flex flex-wrap justify-center gap-2">
                    <Link
                      to="/inventory/receive"
                      className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[var(--accent)] px-4 text-sm font-medium text-[var(--accent-contrast)] hover:opacity-90"
                    >
                      <Truck className="h-4 w-4" />
                      {t('Add stock')}
                    </Link>
                    <Button variant="secondary" onClick={() => openEditor(null)}>
                      <Plus className="h-4 w-4" />
                      {t('New product')}
                    </Button>
                  </div>
                ) : undefined
              }
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {products.page.map((product) => (
                <li
                  key={product.id}
                  className="surface-raised flex items-center gap-3 rounded-[var(--radius-card)] border p-3"
                >
                  <button
                    type="button"
                    onClick={() => canManageCatalog && openEditor(product)}
                    disabled={!canManageCatalog}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate font-medium">{product.name}</span>
                        {!product.isActive && <Badge tone="neutral">{t('Inactive')}</Badge>}
                        {product.trackExpiry && <Badge tone="accent">{t('Perishable')}</Badge>}
                        {/* An item with no barcode cannot be scanned, which is invisible until a
                            queue is waiting. Said here, where it can still be fixed. */}
                        {!product.primaryBarcode && <Badge tone="warn">{t('No barcode')}</Badge>}
                      </div>
                      <p className="tabular mt-0.5 truncate text-xs text-[var(--text-secondary)]">
                        {product.sku}
                        {product.primaryBarcode ? ` · ${product.primaryBarcode}` : ''} ·{' '}
                        {product.categoryName}
                      </p>
                    </div>

                    <div className="shrink-0 text-right">
                      <p className="tabular font-medium">{formatUsd(product.priceUsd)}</p>
                      <p className="tabular text-xs text-[var(--text-secondary)]">
                        {t('cost')} {formatUsd(product.costUsd)}
                      </p>
                    </div>

                    <div className="w-20 shrink-0 text-right">
                      <p
                        className={
                          product.quantityOnHand <= product.reorderLevel
                            ? 'tabular font-semibold text-warn-500'
                            : 'tabular font-semibold'
                        }
                      >
                        {formatQuantity(product.quantityOnHand)}
                      </p>
                      <p className="text-xs text-[var(--text-secondary)]">{t('on hand')}</p>
                    </div>
                  </button>

                  {canRecordWaste && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Write off ${product.name}`}
                      onClick={() => setWasting(product)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}

                  {canManageCatalog && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Barcode and labels for ${product.name}`}
                      onClick={() => setLabelling(product)}
                    >
                      <Barcode className="h-4 w-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <Pager list={products} />
        </>
      )}

      {tab === 'low' && (
        <>
          <Input
            value={lowSearch}
            onChange={(event) => setLowSearch(event.target.value)}
            placeholder={t('Search by name or SKU')}
            leading={<Search className="h-4 w-4" />}
            wrapperClassName="mb-4"
            autoComplete="off"
          />

          {lowStock.page.length === 0 ? (
            <EmptyState
              icon={TriangleAlert}
              title={t('Nothing is running low')}
              description={t('Products appear here once they drop to or below their reorder level.')}
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {lowStock.page.map((item) => (
                <li
                  key={item.productId}
                  className="surface-raised flex items-center gap-3 rounded-[var(--radius-card)] border p-3"
                >
                  <TriangleAlert className="h-5 w-5 shrink-0 text-warn-500" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{item.name}</p>
                    <p className="tabular text-xs text-[var(--text-secondary)]">{item.sku}</p>
                  </div>
                  <p className="tabular shrink-0 text-right text-sm">
                    <span className="font-semibold text-warn-500">
                      {formatQuantity(item.quantityOnHand)}
                    </span>
                    <span className="text-[var(--text-secondary)]">
                      {' '}
                      / {formatQuantity(item.reorderLevel)}
                    </span>
                  </p>
                </li>
              ))}
            </ul>
          )}

          <Pager list={lowStock} />
        </>
      )}

      {tab === 'expiring' && (
        <>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <Select
              label={t('Within')}
              value={String(expiringDays)}
              onChange={(event) => setExpiringDays(Number(event.target.value))}
              wrapperClassName="w-40"
            >
              <option value="7">{t('7 days')}</option>
              <option value="30">{t('30 days')}</option>
              <option value="90">{t('90 days')}</option>
            </Select>

            <Input
              label={t('Search')}
              value={expiringSearch}
              onChange={(event) => setExpiringSearch(event.target.value)}
              placeholder={t('Product or batch number')}
              leading={<Search className="h-4 w-4" />}
              wrapperClassName="min-w-52 flex-1"
              autoComplete="off"
            />
          </div>

          {expiring.page.length === 0 ? (
            <EmptyState
              icon={CalendarClock}
              title={t('Nothing expiring soon')}
              description={t('Batches with an expiry date within 30 days appear here, soonest first.')}
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {expiring.page.map((batch) => (
                <li
                  key={batch.batchId}
                  className="surface-raised flex items-center gap-3 rounded-[var(--radius-card)] border p-3"
                >
                  <CalendarClock
                    className={
                      batch.daysRemaining < 0
                        ? 'h-5 w-5 shrink-0 text-danger-500'
                        : 'h-5 w-5 shrink-0 text-warn-500'
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{batch.productName}</p>
                    <p className="tabular text-xs text-[var(--text-secondary)]">
                      {batch.expiryDate.slice(0, 10)}
                      {batch.batchNumber ? ` · batch ${batch.batchNumber}` : ''}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <Badge tone={batch.daysRemaining < 0 ? 'danger' : 'warn'}>
                      {batch.daysRemaining < 0
                        ? `${Math.abs(batch.daysRemaining)}d overdue`
                        : `${batch.daysRemaining}d left`}
                    </Badge>
                    <p className="tabular mt-1 text-xs text-[var(--text-secondary)]">
                      {formatQuantity(batch.quantityOnHand)} × {formatUsd(batch.unitCostUsd)}
                    </p>
                  </div>

                  {/* A date keyed wrong at the delivery is not something to live with for the
                      life of the batch: it decides what the till sells first and what shows up
                      here. Corrections are audited by name. */}
                  {canManageStock && (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Change the expiry date on ${batch.productName}`}
                      title={t('Change the expiry date')}
                      onClick={() => setDatingBatch(batch)}
                    >
                      <CalendarCog className="h-4 w-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <Pager list={expiring} />
        </>
      )}

      {tab === 'waste' && canRecordWaste && <WasteHistory />}

      {tab === 'deliveries' && canManageStock && (
        <ListFilters
          value={deliveryFilters}
          onChange={setDeliveryFilters}
          searchHint={t('Delivery number, invoice or supplier')}
        />
      )}

      {tab === 'deliveries' && canManageStock && (
        deliveries.page.length === 0 ? (
          <EmptyState
            icon={Truck}
            title={t('No deliveries yet')}
            description={t('Received stock appears here. A delivery posted by mistake can be unposted, as long as none of it has been sold.')}
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {deliveries.page.map((delivery) => (
              <li
                key={delivery.id}
                className="surface-raised flex flex-wrap items-center gap-3 rounded-[var(--radius-card)] border p-3"
              >
                <Truck className="h-4 w-4 shrink-0 text-[var(--text-secondary)]" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`tabular font-medium ${
                        delivery.status === 'Cancelled' ? 'text-[var(--text-secondary)] line-through' : ''
                      }`}
                    >
                      {delivery.number}
                    </span>
                    {delivery.status === 'Draft' && <Badge tone="warn">{t('Draft')}</Badge>}
                    {delivery.status === 'Posted' && <Badge tone="success">{t('Posted')}</Badge>}
                    {delivery.status === 'Cancelled' && (
                      <ReversedBadge reason={delivery.reversalReason} by={delivery.reversedBy} />
                    )}
                    {/* Said on the row rather than only in the payables report, because this is
                        the list someone is looking at when a supplier turns up for their money. */}
                    {delivery.isOutstanding && <Badge tone="warn">{t('Owed')}</Badge>}
                    {delivery.settledAtUtc && <Badge tone="neutral">{t('Paid')}</Badge>}
                  </div>
                  <p className="truncate text-xs text-[var(--text-secondary)]">
                    {delivery.receiptDate.slice(0, 10)} · {delivery.supplierName ?? 'No supplier'} ·{' '}
                    {delivery.lineCount} lines
                    {delivery.supplierInvoiceNumber ? ` · inv ${delivery.supplierInvoiceNumber}` : ''}
                    {delivery.reversalReason ? ` · unposted: ${delivery.reversalReason}` : ''}
                  </p>

                  {/* What was actually in it. Until this was here there was nowhere in the app to
                      see what a delivery charged, so an item's cost — which is the average of
                      exactly these figures — could not be checked against anything. */}
                  <button
                    type="button"
                    className="mt-1 text-xs font-medium text-[var(--accent)]"
                    onClick={() => setOpenDelivery(openDelivery === delivery.id ? null : delivery.id)}
                  >
                    {openDelivery === delivery.id ? t('Hide what was in it') : t('See what was in it')}
                  </button>

                  {openDelivery === delivery.id && (
                    <ul className="surface-sunken mt-2 flex flex-col gap-1 rounded-lg p-2">
                      {delivery.lines.map((line) => (
                        <li key={line.productId} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                          <span className="min-w-0 flex-1 truncate">{line.productName}</span>
                          <span className="tabular">
                            {formatQuantity(line.quantity)} × <bdi>{formatUsd(line.unitCostUsd)}</bdi>
                          </span>
                          {/* Only when carriage actually moved it, or the second figure reads as a
                              mistake rather than as the share of the delivery charge. */}
                          {line.landedUnitCostUsd !== line.unitCostUsd && (
                            <span className="tabular text-[var(--text-secondary)]">
                              {t('landed')} <bdi>{formatUsd(line.landedUnitCostUsd)}</bdi>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <span
                  className={`tabular font-semibold ${
                    delivery.status === 'Cancelled' ? 'text-[var(--text-secondary)] line-through' : ''
                  }`}
                >
                  {formatUsd(delivery.totalCostUsd)}
                </span>

                {delivery.isOutstanding && canManageStock && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      setSettling({
                        id: delivery.id,
                        what: `${delivery.number} · ${delivery.supplierName ?? 'No supplier'}`,
                        amountUsd: delivery.totalCostUsd,
                      })
                    }
                  >
                    <HandCoins className="h-4 w-4" />
                    {t('Pay it')}
                  </Button>
                )}

                {/* Only a posted delivery has stock to take back; a draft is edited, not undone. */}
                {delivery.status === 'Posted' && (
                  <ReverseButton
                    isReversed={false}
                    label={`Unpost ${delivery.number}`}
                    onClick={() => setUnposting(delivery)}
                  />
                )}
              </li>
            ))}
          </ul>
        )
      )}

      {tab === 'deliveries' && canManageStock && (
        <Pager list={deliveries} />
      )}

      <ExpiryDialog batch={datingBatch} onClose={() => setDatingBatch(null)} />

      <SettleDialog
        target={settling}
        endpoint={(id) => `/api/goods-receipts/${id}/settle`}
        invalidate={['goods-receipts']}
        onClose={() => setSettling(null)}
      />

      <ProductEditor
        open={isEditorOpen}
        product={editing}
        onClose={() => setEditorOpen(false)}
      />

      <LabelDialog product={labelling} onClose={() => setLabelling(null)} />

      <WasteDialog product={wasting} onClose={() => setWasting(null)} />

      <ReverseDialog
        kind="goodsReceipt"
        id={unposting?.id ?? null}
        what={
          unposting
            ? `${unposting.number} from ${unposting.supplierName ?? 'a supplier'} — ${formatUsd(unposting.totalCostUsd)}`
            : ''
        }
        onClose={() => setUnposting(null)}
        invalidate={['goods-receipts', 'products', 'stock', 'low-stock', 'reports']}
      />
    </div>
  )
}

/**
 * Corrects the expiry date on a batch already in stock.
 *
 * Expiry belongs to a batch, not to a product — two deliveries of the same yoghurt go off on
 * different days — so this is the only place it can honestly be edited. Blanking it is allowed
 * for stock that turned out not to perish; the server records who changed it either way, since
 * pushing a date out is also how out-of-date stock gets quietly kept on a shelf.
 */
function ExpiryDialog({
  batch,
  onClose,
}: {
  batch: ExpiringBatch | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [date, setDate] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (batch) {
      setDate(batch.expiryDate.slice(0, 10))
      setError(null)
    }
  }, [batch])

  const save = useMutation({
    mutationFn: () =>
      apiClient.put(`/api/stock/batches/${batch!.batchId}/expiry`, {
        expiryDate: date === '' ? null : date,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['stock'] })
      onClose()
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not save that date.',
      ),
  })

  return (
    <Modal
      open={batch !== null}
      onClose={onClose}
      title={t('Expiry date')}
      description={batch ? `${batch.productName}${batch.batchNumber ? ` · batch ${batch.batchNumber}` : ''}` : ''}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <Input
        label={t('Expires')}
        type="date"
        value={date}
        autoFocus
        onChange={(event) => setDate(event.target.value)}
        hint={t('Leave blank if this stock does not go off. Stock is always sold soonest-expiry first.')}
        error={error ?? undefined}
      />
    </Modal>
  )
}
